import cx from 'classnames';
import type { FC, PropsWithChildren, SyntheticEvent } from 'react';
import { Link } from 'react-router';

import type { LocationDescriptor } from '@zen/types';
import { extractToAndState } from '@zen/utils/routing';

import Icon from '../Icon';

interface Props {
  className?: string;
  color?: 'dark' | 'default' | 'white';
  isExternal?: boolean;
  isStandalone?: boolean;
  linkTo?: LocationDescriptor;
  onClick?: (event: SyntheticEvent) => void;
  underline?: 'default' | 'always' | 'never';
}

const TextLink: FC<PropsWithChildren<Props>> = (props) => {
  const {
    children,
    isExternal = false,
    linkTo,
    onClick,
    isStandalone = true,
    className,
    color = 'default',
    underline = 'default'
  } = props;

  const textLinkWrapperClassName: string = cx(
    'text-sm group/text-wrap cursor-pointer',
    {
      'text-grey-dark hover:text-grey-dark': color === 'dark',
      'text-navy-base hover:text-azure-base': color === 'default',
      'text-white hover:text-white': color === 'white'
    },
    className
  );

  const textLinkClassName: string = cx('border-solid border-b', {
    // Default behavior (maintains backward compatibility)
    'border-transparent group-hover/text-wrap:border-azure-base': underline === 'default' && isStandalone && color === 'default',
    'border-navy-base group-hover/text-wrap:border-transparent': underline === 'default' && !isStandalone && color === 'default',
    // White and dark variants default behavior: underlined by default, remove on hover
    'border-white group-hover/text-wrap:border-transparent': underline === 'default' && color === 'white',
    'border-grey-dark group-hover/text-wrap:border-transparent': underline === 'default' && color === 'dark',
    // Always show underline
    'border-grey-dark group-hover/text-wrap:border-grey-dark': underline === 'always' && color === 'dark',
    'border-navy-base group-hover/text-wrap:border-azure-base': underline === 'always' && color === 'default',
    'border-white group-hover/text-wrap:border-grey-lightest': underline === 'always' && color === 'white',
    // Never show underline
    'border-transparent': underline === 'never'
  });

  const TextLinkContent = (
    <span className={textLinkClassName} data-testid="text-link-content">
      {children}
    </span>
  );

  if (isExternal) {
    return (
      <a
        className={textLinkWrapperClassName}
        data-component="text-link"
        data-testid="text-link"
        href={linkTo as string}
        onClick={onClick}
        target="_blank"
        rel="noreferrer"
      >
        {TextLinkContent}
        <Icon className="ml-1 text-sm" icon="zicon-diagonal-arrow" />
      </a>
    );
  }

  const handleClick = (event: SyntheticEvent): void => {
    onClick?.(event);
  };

  if (linkTo) {
    return (
      <Link
        className={textLinkWrapperClassName}
        data-component="text-link"
        data-testid="text-link"
        onClick={handleClick}
        {...extractToAndState(linkTo)}
      >
        {TextLinkContent}
      </Link>
    );
  }

  return (
    <span className={textLinkWrapperClassName} data-component="text-link" data-testid="text-link" onClick={handleClick}>
      {TextLinkContent}
    </span>
  );
};

export type { Props as TextLinkProps };

export default TextLink;
